import type {
  QueryClient,
  QueryKey,
  QueryFunction,
} from '@tanstack/react-query';

export interface AbsentQueryPrefetchOptions<T> {
  queryKey: QueryKey;
  queryFn: QueryFunction<T>;
  staleTime?: number;
  gcTime?: number;
}

/**
 * Warm a query only after React Query has garbage-collected its exact cache entry.
 *
 * This deliberately does not inspect freshness. A stale, pending, or failed entry
 * still belongs to React Query's normal mount/invalidation/reconnect lifecycle and
 * must not be turned into an extra hover-refresh policy.
 */
export function prefetchQueryIfAbsent<T>(
  queryClient: QueryClient,
  options: AbsentQueryPrefetchOptions<T>,
): Promise<void> {
  const existing = queryClient.getQueryCache().find({
    queryKey: options.queryKey,
    exact: true,
  });
  if (existing) return Promise.resolve();

  return queryClient.prefetchQuery(options);
}

